/**
 * U2 — TOUTE ÉCRITURE ALLUME LE TÉMOIN, SANS EXCEPTION.
 *
 * ── Le défaut, mesuré avant d'y toucher (25/08/2026) ───────────────────────
 *
 * « Il faut que toute l'app se comporte de la même manière pour les boutons
 * d'actions et leur réaction (sablier, petit message de prise en compte). »
 *
 * Le témoin d'attente existait déjà (`TravailEnCours` : un tube et sa goutte,
 * après 400 ms d'attente réelle). Mais il ne s'allumait que pour les écritures
 * passant par `smq`/`smqData` — **82 appels**. **Quatre-vingt-six autres
 * écritures**, réparties sur une trentaine d'écrans, appelaient `fetch`
 * directement : aucun témoin, donc rien à l'écran entre le clic et la réponse.
 * C'est exactement ce qui donne l'impression d'une application qui ne réagit
 * pas toujours pareil — et ce qui fait recliquer.
 *
 * ── Pourquoi un ENROBAGE et non un passage par `smq` ───────────────────────
 *
 * La tentation était de faire passer les 86 par `smq`. Elle est écartée, et
 * pour une raison de fond : `smq` appelle `refresh()`, un rechargement GLOBAL
 * du magasin. Ces écrans-là tiennent leur propre état — le registre des
 * grilles, la composition d'un poste, une série de contrôle — et le rechargent
 * eux-mêmes, précisément. Les faire passer par `smq` déclencherait un
 * rechargement général à chaque clic, plus lent, et qui ne rapatrie même pas ce
 * dont l'écran a besoin. Ce n'est pas un oubli s'ils ne l'utilisent pas.
 *
 * `fetchEcriture` ne fait donc qu'UNE chose de plus que `fetch` : incrémenter
 * le compteur d'écritures pendant l'appel. Elle rend la MÊME `Response`, ce qui
 * la rend substituable sans toucher à une seule ligne du traitement de la
 * réponse — les écrans gardent leur `r.ok`, leur lecture du corps et leurs
 * messages.
 *
 * ⚠️ LE COMPTEUR DESCEND DANS UN `finally`. Sur le chemin d'erreur comme sur
 * celui du succès : un témoin resté allumé après un échec réseau ferait croire
 * que l'application travaille encore, et l'on attendrait indéfiniment devant un
 * écran mort. C'est le même contrat que `smq`.
 *
 * ⚠️ ELLE N'AFFICHE AUCUN MESSAGE. Le message de succès dépend de ce que
 * l'écran vient de faire — « Composition enregistrée », « Série close » — et
 * une phrase générique posée ici les remplacerait toutes par « Enregistré »,
 * ce qui est moins utile que ce que les écrans disent déjà.
 */
import { useGed } from '@/lib/store';

/**
 * `fetch`, plus le témoin « l'application travaille ».
 *
 * Substituable à `fetch` partout où l'appel ÉCRIT. Sur une lecture, elle
 * allumerait le témoin pour rien — et le témoin cesserait de vouloir dire
 * « une écriture est en cours », qui est tout son sens.
 */
export async function fetchEcriture(
  entree: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const { setState, getState } = useGed;
  setState({ ecrituresEnCours: getState().ecrituresEnCours + 1 });
  try {
    return await fetch(entree, init);
  } finally {
    setState({ ecrituresEnCours: Math.max(0, getState().ecrituresEnCours - 1) });
  }
}
